import React from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { useLeague } from '../hooks/useLeague';
import type { LscLeague } from '../types';
import { lscPath } from '../routing';
import {
  LEAGUE_SWITCHER_ARIA_LABEL,
  LEAGUE_SWITCHER_PLACEHOLDER,
  leagueSwitcherActiveLabel,
  leagueSwitcherHelperCopy,
  leagueWorkspaceContextCopy,
} from '../leagueWorkspaceCopy';

interface LeagueSwitcherProps {
  compact?: boolean;
  showContextCopy?: boolean;
}

const LeagueSwitcher: React.FC<LeagueSwitcherProps> = ({ compact, showContextCopy = !compact }) => {
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { leagues, league } = useLeague();

  const goToLeague = (id: string) => {
    navigate(lscPath(`/leagues/${id}/command-center`));
  };

  const activeName = league?.name ?? leagues.find((item) => item.id === leagueId)?.name;
  const contextCopy = leagueWorkspaceContextCopy(leagues.length, activeName);
  const helperCopy = leagueSwitcherHelperCopy(leagues.length);
  const activeLabel = leagueSwitcherActiveLabel(leagues.length, activeName);

  if (!leagues.length) {
    return (
      <div className="space-y-2" data-testid="league-switcher-empty">
        <p className="text-xs uppercase tracking-wide text-secondary-500">{LEAGUE_SWITCHER_ARIA_LABEL}</p>
        <p className="text-sm text-secondary-400">{contextCopy}</p>
        <Link
          to={lscPath('/leagues/new')}
          className="inline-flex min-h-11 items-center text-sm text-primary-300 hover:text-primary-200"
          data-testid="league-switcher-create-cta"
        >
          Start a league
        </Link>
        <Link
          to={lscPath('/join')}
          className="inline-flex min-h-11 items-center text-sm text-secondary-400 hover:text-secondary-300"
          data-testid="league-switcher-join-cta"
        >
          Enter invite code
        </Link>
      </div>
    );
  }

  const label = (item: LscLeague) => item.name;

  if (leagues.length === 1 && !compact) {
    const onlyLeague = leagues[0];
    return (
      <div className="space-y-2" data-testid="league-switcher-single">
        <p className="text-xs uppercase tracking-wide text-secondary-500">Current workspace</p>
        <p className="text-sm font-medium text-white truncate" data-testid="league-switcher-active-label">
          {onlyLeague.name}
        </p>
        {onlyLeague.game_label || onlyLeague.game_key ? (
          <p className="text-xs text-secondary-500 truncate">
            {onlyLeague.game_label ?? onlyLeague.game_key}
          </p>
        ) : null}
        {showContextCopy ? (
          <p className="text-xs text-secondary-500" data-testid="league-switcher-context-copy">
            {contextCopy}
          </p>
        ) : null}
      </div>
    );
  }

  const selectControl = (
    <div className="relative min-w-0">
      <select
        className={`input-field text-sm min-h-11 pr-9 appearance-none ${
          compact ? 'max-w-full' : 'w-full'
        }`}
        value={leagueId ?? ''}
        onChange={(e) => {
          if (e.target.value) {
            goToLeague(e.target.value);
          }
        }}
        aria-label={LEAGUE_SWITCHER_ARIA_LABEL}
        data-testid="league-switcher-select"
      >
        {!leagueId && <option value="">{LEAGUE_SWITCHER_PLACEHOLDER}</option>}
        {leagues.map((item) => (
          <option key={item.id} value={item.id}>
            {label(item)}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
        aria-hidden
      />
    </div>
  );

  if (compact) {
    return (
      <div className="min-w-0 w-full space-y-1" data-testid="league-switcher-compact">
        {helperCopy ? (
          <p className="text-[11px] uppercase tracking-wide text-secondary-500">{helperCopy}</p>
        ) : null}
        <p
          className="truncate text-sm font-medium text-white md:hidden"
          data-testid="league-switcher-active-label"
        >
          {activeLabel}
        </p>
        {selectControl}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="league-switcher-multi">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-secondary-500">
          {helperCopy ?? 'Current workspace'}
        </p>
        {leagues.length > 1 ? (
          <span className="text-[11px] text-secondary-500" data-testid="league-switcher-count">
            {leagues.length} leagues
          </span>
        ) : null}
      </div>
      <p className="text-sm font-medium text-white truncate" data-testid="league-switcher-active-label">
        {activeLabel}
      </p>
      {selectControl}
      {league && (
        <p className="text-xs text-secondary-500 truncate">{league.game_label ?? league.game_key}</p>
      )}
      {showContextCopy ? (
        <p className="text-xs text-secondary-500" data-testid="league-switcher-context-copy">
          {contextCopy}
        </p>
      ) : null}
    </div>
  );
};

export default LeagueSwitcher;
